import TooltipWithMarkdown from '@unifyapps/carbon/components/TooltipWithMarkdown';
import { dynamic } from '@unifyapps/carbon/external/dynamic';
import type { BlockRendererWrapperProps } from '@unifyapps/carbon/no-code/context/RegistryProvider/types';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { isEmptyBlock } from '@unifyapps/carbon/no-code/utils/block';
import { isVisible } from '@unifyapps/carbon/no-code/utils/visibility';
import type { BlockTooltipAddOn } from '@unifyapps/defs/blocks/common/tooltipTypes';
import type { BlockId } from '@unifyapps/defs/types/block';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { useState } from 'react';

const SLOT_TOOLTIP_SLOT_PROPS = { rootClassName: '!p-0 !max-w-none !bg-transparent !shadow-none' };

const TooltipBuilderContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "TooltipBuilderContainer" */
      '../components/TooltipBuilderContainer'
    ),
  { loading: () => null },
);

function getBlockTooltipContent({
  blockId,
  tooltipAddOn,
  tooltipSlotBlockId,
  forceOpen,
  showOnHover,
  BlockRenderer,
  children,
}: {
  blockId: string;
  tooltipAddOn: BlockTooltipAddOn | undefined;
  tooltipSlotBlockId: string | null | undefined;
  forceOpen: boolean;
  showOnHover: boolean;
  BlockRenderer: ComponentType<BlockRendererWrapperProps>;
  children: ReactElement;
}): ReactNode {
  const tooltipType = tooltipAddOn?.type ?? 'text';
  const hasSlotContent =
    tooltipType === 'slot' && Boolean(tooltipSlotBlockId) && !isEmptyBlock(tooltipSlotBlockId);
  const textContent = tooltipType === 'text' ? tooltipAddOn?.content : undefined;

  if (!hasSlotContent && !textContent) {
    return children;
  }

  const showTooltip = isVisible(tooltipAddOn?.appearance?.visibility);
  const placement =
    tooltipAddOn?.placementBehavior === 'manual' ? tooltipAddOn.placementPosition : undefined;

  const tooltipSlotNode = hasSlotContent ? (
    <BlockRenderer blockId={tooltipSlotBlockId as BlockId} parentBlockId={blockId as BlockId} />
  ) : null;

  const showOnHoverAndVisible = showOnHover && showTooltip;
  const shouldShowSlotTooltip = forceOpen || showOnHoverAndVisible;

  if (hasSlotContent && shouldShowSlotTooltip) {
    return (
      // remount on forceOpen change so MUI's useControlled re-evaluates controlled mode correctly
      // (it only evaluates on first render, so a stale mount would ignore later 'open' change)
      <Tooltip
        arrow={false}
        key={forceOpen ? 'force-open' : 'hover'}
        offset={tooltipAddOn?.appearance?.offset}
        open={forceOpen || undefined}
        placement={placement}
        slotProps={SLOT_TOOLTIP_SLOT_PROPS}
        titleNode={tooltipSlotNode}
      >
        {children}
      </Tooltip>
    );
  }

  if (textContent && showTooltip) {
    return (
      <TooltipWithMarkdown description={textContent} placement={placement}>
        {children}
      </TooltipWithMarkdown>
    );
  }

  return children;
}

export function useBlockTooltip({
  blockId,
  tooltipAddOn,
  tooltipSlotBlockId,
  BlockRenderer,
}: {
  blockId: string;
  tooltipAddOn: BlockTooltipAddOn | undefined;
  tooltipSlotBlockId: string | null | undefined;
  BlockRenderer: ComponentType<BlockRendererWrapperProps>;
}) {
  const mode = useInterfaceStore().hooks.useMode();
  const isBuilder = mode === InterfaceModes.BUILDER;
  const [isTooltipSlotOrDescendantActive, setIsTooltipSlotOrDescendantActive] = useState(false);
  const [showTooltipOnHover, setShowTooltipOnHover] = useState(!isBuilder);

  // exposed as a wrapper function (not a precomputed node) so a block with multiple render
  // paths — like Typography's markdown / html / truncated / plain branches — can wrap each of
  // them with the same tooltip. The tooltip's child must stay a single ref-forwarding element,
  // so callers keep any Suspense boundary outside this call.
  const wrapWithTooltip = (children: ReactElement): ReactNode =>
    getBlockTooltipContent({
      blockId,
      tooltipAddOn,
      tooltipSlotBlockId,
      forceOpen: isTooltipSlotOrDescendantActive,
      showOnHover: showTooltipOnHover,
      BlockRenderer,
      children,
    });

  const tooltipBuilderNode = isBuilder ? (
    <TooltipBuilderContainer
      setIsTooltipSlotOrDescendantActive={setIsTooltipSlotOrDescendantActive}
      setShowTooltipOnHover={setShowTooltipOnHover}
      tooltipSlotBlockId={tooltipSlotBlockId}
    />
  ) : null;

  return { wrapWithTooltip, isBuilder, tooltipBuilderNode };
}
